import { ObjectivePhoriaResult, TestType } from "../../../types/testTypes";
import { calculateAgeForTest, displayPatientName, formatLongDate } from "../../../utils/CommonUtil";
import { useEffect, useMemo, useState } from "react";

import { useSearchParams } from "react-router-dom";
import useTestList from "../../../hooks/useTestList";
import {
  AgeBand,
  RiskFinding,
  clampVaLogMarToFloor,
  combineLazyEyeRisk,
  coverTestFinding,
  describeFixationStability,
  getAgeBand,
  phoriaFinding,
  vaInterocularFinding,
  vaMonocularFinding,
  vaStatusForAge,
} from "../utils/lazyEyeRiskModel";
import { VaStatus, getVaLogMar } from "@utils/test-specific-utils/VisualAcuityUtils";
import {
  CoverTestMetrics,
  computeCoverTestMetrics,
} from "@utils/test-specific-utils/CoverTestMetricsUtils";

function extractFirstTest(list: any): any | null {
  const tests = Array.isArray(list) ? list : list?.data;
  return Array.isArray(tests) && tests.length > 0 ? tests[0] : null;
}

export interface PatientData {
  name: string;
  age: number;
  testDate: string;
  testId: string;
}

export type { CoverTestMetrics } from "@utils/test-specific-utils/CoverTestMetricsUtils";

export interface CoverTestData {
  overallMetrics: CoverTestMetrics;
  leftEyeMetrics: CoverTestMetrics;
  rightEyeMetrics: CoverTestMetrics;
  isDataModeInDegrees: boolean;
  overallFixationDescriptor: string;
  leftFixationDescriptor: string;
  rightFixationDescriptor: string;
}

export function useLazyEyeData(
  overrideCoverTestUuid?: string | null,
  overrideLeftVaUuid?: string | null,
  overrideRightVaUuid?: string | null,
) {
  const [searchParams] = useSearchParams();
  const uuid = searchParams.get("uuid");
  const coverTestUuid = overrideCoverTestUuid ?? searchParams.get("coverTestUuid");
  const leftVaUuid = overrideLeftVaUuid ?? searchParams.get("leftVaUuid");
  const rightVaUuid = overrideRightVaUuid ?? searchParams.get("rightVaUuid");

  const primaryUuid = uuid || coverTestUuid;

  const { testList, testListLoading } = useTestList(primaryUuid ? { uuid: primaryUuid } : {});
  const { testList: coverTestList, testListLoading: coverTestListLoading } = useTestList(
    coverTestUuid ? { uuid: coverTestUuid } : {},
  );
  const { testList: leftVaTestList, testListLoading: leftVaTestListLoading } = useTestList(
    leftVaUuid ? { uuid: leftVaUuid } : {},
  );
  const { testList: rightVaTestList, testListLoading: rightVaTestListLoading } = useTestList(
    rightVaUuid ? { uuid: rightVaUuid } : {},
  );

  const [patientData, setPatientData] = useState<PatientData>({
    name: "Patient",
    age: 0,
    testDate: formatLongDate(new Date()),
    testId: "LE-2025-0000",
  });

  const [vaTest1, setVaTest1] = useState<any>(null);
  const [vaTest2, setVaTest2] = useState<any>(null);
  const [isPhoria, setIsPhoria] = useState(false);
  const [phoriaData, setPhoriaData] = useState<ObjectivePhoriaResult | null>(null);

  const [coverTestData, setCoverTestData] = useState<CoverTestData>({
    overallMetrics: { mean: Number.NaN, stability: Number.NaN },
    leftEyeMetrics: { mean: Number.NaN, stability: Number.NaN },
    rightEyeMetrics: { mean: Number.NaN, stability: Number.NaN },
    isDataModeInDegrees: true,
    overallFixationDescriptor: "N/A",
    leftFixationDescriptor: "N/A",
    rightFixationDescriptor: "N/A",
  });

  useEffect(() => {
    const test = extractFirstTest(testList);
    if (test) {
      const patientAge = test.patient?.dob
        ? calculateAgeForTest(new Date(test.startDate), new Date(test.patient.dob))
        : 0;

      const testDate = new Date(test.startDate);
      const testId = `LE-${testDate.getFullYear()}-${String(testDate.getMonth() + 1).padStart(2, "0")}${String(testDate.getDate()).padStart(2, "0")}`;

      setPatientData({
        name: test.patient?.name ? displayPatientName(test.patient.name) : "Patient",
        age: patientAge,
        testDate: formatLongDate(testDate),
        testId,
      });
    }
  }, [testList]);

  useEffect(() => {
    const test = extractFirstTest(coverTestList);
    if (!test) return;

    const testIsPhoria = test.testType === TestType.ObjectivePhoria;
    setIsPhoria(testIsPhoria);

    const result = test.results;
    if (!result) return;

    if (testIsPhoria && result.objectivePhoria) {
      setPhoriaData(result.objectivePhoria);
      return;
    }

    const { overall, leftEye, rightEye, isDataModeInDegrees } = computeCoverTestMetrics(result);
    setCoverTestData({
      overallMetrics: overall,
      leftEyeMetrics: leftEye,
      rightEyeMetrics: rightEye,
      isDataModeInDegrees,
      overallFixationDescriptor: describeFixationStability(overall.stability, isDataModeInDegrees),
      leftFixationDescriptor: describeFixationStability(leftEye.stability, isDataModeInDegrees),
      rightFixationDescriptor: describeFixationStability(rightEye.stability, isDataModeInDegrees),
    });
  }, [coverTestList]);

  useEffect(() => {
    setVaTest1(extractFirstTest(leftVaTestList));
  }, [leftVaTestList]);

  useEffect(() => {
    setVaTest2(extractFirstTest(rightVaTestList));
  }, [rightVaTestList]);

  const ageBand: AgeBand = useMemo(() => getAgeBand(patientData.age), [patientData.age]);

  const vaLogMar1 = useMemo(() => clampVaLogMarToFloor(getVaLogMar(vaTest1)), [vaTest1]);
  const vaLogMar2 = useMemo(() => clampVaLogMarToFloor(getVaLogMar(vaTest2)), [vaTest2]);

  const findings = useMemo(() => {
    const vaInteroc: RiskFinding | null = vaInterocularFinding(vaLogMar1, vaLogMar2);
    const coverTest: RiskFinding | null =
      coverTestUuid && !isPhoria && Number.isFinite(coverTestData.overallMetrics.mean)
        ? coverTestFinding(
            coverTestData.overallMetrics.mean,
            coverTestData.overallMetrics.stability,
            coverTestData.isDataModeInDegrees,
          )
        : null;
    const phoria: RiskFinding | null =
      isPhoria && phoriaData ? phoriaFinding(phoriaData, ageBand) : null;
    const worstMonoSeverity = [vaLogMar1, vaLogMar2]
      .map((v) => vaMonocularFinding(v, ageBand))
      .filter((f): f is RiskFinding => f !== null)
      .reduce<RiskFinding | null>(
        (acc, f) => (acc === null || f.severity > acc.severity ? f : acc),
        null,
      );
    return {
      vaInteroc,
      coverTest,
      phoria,
      monocularVa: worstMonoSeverity,
    };
  }, [
    vaLogMar1,
    vaLogMar2,
    coverTestUuid,
    isPhoria,
    coverTestData.overallMetrics.mean,
    coverTestData.overallMetrics.stability,
    coverTestData.isDataModeInDegrees,
    phoriaData,
    ageBand,
  ]);

  const vaStatusTokens = useMemo(
    () =>
      [vaLogMar1, vaLogMar2]
        .map((logMar) => vaStatusForAge(logMar, ageBand))
        .filter((status): status is VaStatus => status !== null),
    [vaLogMar1, vaLogMar2, ageBand],
  );

  const interocDiffLogMar = useMemo(
    () => (vaLogMar1 != null && vaLogMar2 != null ? Math.abs(vaLogMar1 - vaLogMar2) : null),
    [vaLogMar1, vaLogMar2],
  );

  const combined = useMemo(() => combineLazyEyeRisk(findings, ageBand), [findings, ageBand]);

  const loading =
    testListLoading || coverTestListLoading || leftVaTestListLoading || rightVaTestListLoading;

  return {
    patientData,
    ageBand,
    coverTestData,
    vaTest1,
    vaTest2,
    vaLogMar1,
    vaLogMar2,
    vaStatusTokens,
    interocDiffLogMar,
    isPhoria,
    phoriaData,
    findings,
    combined,
    hasCoverTest: !!coverTestUuid,
    hasVaTest1: !!leftVaUuid,
    hasVaTest2: !!rightVaUuid,
    loading,
  };
}
